export const getValidMobileNumber = (mobile: string) => {
  let mobileNumber = mobile;
  if (mobileNumber.length > 10) {
    if (mobileNumber.length === 12 && mobileNumber.indexOf("91") == 0) {
      mobileNumber = mobileNumber.slice(2);
    } else if (
      mobileNumber.length === 13 &&
      (mobileNumber.indexOf("+91") === 0 || mobileNumber.indexOf("91 ") === 0)
    ) {
      mobileNumber = mobileNumber.slice(3);
    } else if (
      mobileNumber.length === 14 &&
      mobileNumber.indexOf("+91 ") === 0
    ) {
      mobileNumber = mobileNumber.slice(4);
    }
  }
  return mobileNumber;
};

// Format date
export function convertDate(dateString: string) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours() % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = date.getHours() >= 12 ? "PM" : "AM";

  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
}

// Interface for exam date structure
export interface ExamDate {
  date: string;
  time: string;
  fullDateTime: Date;
}

// Get current IST time from web API
export const getCurrentISTTime = async (): Promise<Date> => {
  try {
    // Using WorldTimeAPI for IST timezone
    const response = await fetch(
      "https://worldtimeapi.org/api/timezone/Asia/Kolkata"
    );
    if (!response.ok) {
      throw new Error("Failed to fetch time from API");
    }
    const data = await response.json();
    return new Date(data.datetime);
  } catch (error) {
    console.warn(
      "Failed to fetch IST time from API, falling back to local time:",
      error
    );
    // Fallback to local time converted to IST
    const now = new Date();
    // Convert to IST (UTC+5:30)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    return new Date(utc + istOffset);
  }
};

// Parse exam date string to Date object
export const parseExamDate = (dateStr: string, timeStr: string): Date => {
  // Parse date like "9th September 2025"
  const dateMatch = dateStr.match(/(\d+)(?:st|nd|rd|th)\s+(\w+)\s+(\d+)/);
  if (!dateMatch) {
    throw new Error(`Invalid date format: ${dateStr}`);
  }

  const [, day, monthName, year] = dateMatch;
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const monthIndex = monthNames.findIndex(
    (m) => m.toLowerCase() === monthName.toLowerCase()
  );
  if (monthIndex === -1) {
    throw new Error(`Invalid month: ${monthName}`);
  }

  // Parse time like "5:00 PM - 6:00 PM IST" - we'll use the start time
  const timeMatch = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!timeMatch) {
    throw new Error(`Invalid time format: ${timeStr}`);
  }

  const [, hours, minutes, ampm] = timeMatch;
  let hour24 = parseInt(hours);
  if (ampm.toUpperCase() === "PM" && hour24 !== 12) {
    hour24 += 12;
  } else if (ampm.toUpperCase() === "AM" && hour24 === 12) {
    hour24 = 0;
  }

  return new Date(
    parseInt(year),
    monthIndex,
    parseInt(day),
    hour24,
    parseInt(minutes)
  );
};

// Filter upcoming exam dates (excludes current date and past dates)
export const filterUpcomingExamDates = async (
  examDates: ExamDate[]
): Promise<ExamDate[]> => {
  try {
    const currentIST = await getCurrentISTTime();

    // Get start of current day in IST
    const currentDayStart = new Date(currentIST);
    currentDayStart.setHours(0, 0, 0, 0);

    // Get start of next day in IST
    const nextDayStart = new Date(currentDayStart);
    nextDayStart.setDate(nextDayStart.getDate() + 1);

    // Filter dates that are from tomorrow onwards
    return examDates.filter((examDate) => {
      const examDayStart = new Date(examDate.fullDateTime);
      examDayStart.setHours(0, 0, 0, 0);
      return examDayStart >= nextDayStart;
    });
  } catch (error) {
    console.error("Error filtering exam dates:", error);
    // Return all dates if filtering fails
    return examDates;
  }
};

// Static exam dates data
export const EXAM_DATES_DATA: Omit<ExamDate, "fullDateTime">[] = [
  { date: "9th September 2025", time: "5:00 PM - 6:00 PM IST" },
  { date: "29th September 2025", time: "5:00 PM - 6:00 PM IST" },
  { date: "10th October 2025", time: "5:00 PM - 6:00 PM IST" },
  { date: "14th October 2025", time: "5:00 PM - 6:00 PM IST" },
  { date: "6th November 2025", time: "5:00 PM - 6:00 PM IST" },
  { date: "17th November 2025", time: "5:00 PM - 6:00 PM IST" },
  { date: "4th December 2025", time: "5:00 PM - 6:00 PM IST" },
  { date: "18th December 2025", time: "5:00 PM - 6:00 PM IST" },
];

// Process exam dates with full DateTime objects
export const processExamDates = (): ExamDate[] => {
  return EXAM_DATES_DATA.map((examDate) => ({
    ...examDate,
    fullDateTime: parseExamDate(examDate.date, examDate.time),
  }));
};
